import { addDays, format } from "date-fns";
import type React from "react";
import { useMemo } from "react";
import {
	getScenarioDataPointsCount,
	getScenarioDataPointsGraphql,
} from "@/api/services/scenarioDataPointGraphqlService";
import {
	getScenarioTransactionDistinctValues,
	getScenarioTransactionsCount,
	getScenarioTransactionsGraphql,
} from "@/api/services/scenarioTransactionGraphqlService";
import type { ColumnFilters } from "@/components/MaximusTableReusable";
import { SCREEN_NAMES } from "@/constants/screenNames";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useVisibleScenarios } from "@/hooks/useVisibleScenarios";
import {
	getScenarioDemoBalanceRows,
	getScenarioDemoTransactionOptions,
	getScenarioDemoTransactionRows,
} from "../../data/scenarioTransactionsDemoData";
import {
	type QuickFilterRanges,
	TransactionsBalancesTable,
	type TransactionsTableSource,
} from "../forecast/TransactionsBalancesTable";

interface ScenarioTransactionsTableProps {
	/** Scenario whose materialised transactions/balances are shown. */
	scenarioId: string;
	/**
	 * Convenience for the same-page "View Transactions" click on the scenario
	 * chart: when set, forces Transactions view and constrains the table to that
	 * single forecast day.
	 */
	dateFilter?: Date | null;
	/** Optional pre-seeded visible filter (dashboard/other-page navigation). */
	initialColumnFilters?: ColumnFilters;
	/**
	 * Monotonic counter that forces a live refetch (filters intact) when the
	 * scenario's data changes server-side — an adjustment was saved or the
	 * scenario was refreshed/re-materialised.
	 */
	refreshSignal?: number;
}

/**
 * Scenario Transactions table — the exact same Transactions/Balances table used
 * under the 90-day forecast chart, but sourced from the selected scenario's
 * persisted transactions (`vw_ScenarioTransactions`) and chart balances
 * (`vw_ScenarioDataPoints`), scoped to this scenario. Links to the scenario
 * forecast chart via the caller-supplied `dateFilter`.
 */
export const ScenarioTransactionsTable: React.FC<
	ScenarioTransactionsTableProps
> = ({ scenarioId, dateFilter, initialColumnFilters, refreshSignal }) => {
	const numericScenarioId = Number(scenarioId);
	const { baseForecast } = useBaseForecast();
	const scenarios = useVisibleScenarios();
	const scenarioChanges = useMemo(
		() => scenarios.find((s) => s.id === scenarioId)?.changes ?? [],
		[scenarios, scenarioId],
	);

	// Anchor the 7/30/90-day quick-filter windows on the base-forecast start
	// date (falling back to today when not yet available), mirroring the
	// forecast Transactions table.
	const horizonStartDate =
		baseForecast.length > 0 ? baseForecast[0].date : null;
	const quickFilterRanges = useMemo<QuickFilterRanges>(() => {
		const startDate = horizonStartDate ?? new Date();
		const rangeForDays = (days: number) => ({
			ge: format(startDate, "yyyy-MM-dd"),
			le: format(addDays(startDate, days), "yyyy-MM-dd"),
		});
		return {
			"7": rangeForDays(7),
			"30": rangeForDays(30),
			"90": rangeForDays(90),
		};
	}, [horizonStartDate]);

	const source = useMemo<TransactionsTableSource>(
		() => ({
			title: "Scenario Transactions",
			screenName: SCREEN_NAMES.SCENARIO_TRANSACTIONS,
			exportTransactionsForm: "ScenarioTransactions",
			exportBalancesForm: "ScenarioDataPoints",
			exportTransactionsFileName: "scenario-transactions-export",
			exportBalancesFileName: "scenario-balances-export",
			fetchTransactions: (filter, first, after, sort) =>
				getScenarioTransactionsGraphql(
					numericScenarioId,
					filter,
					first,
					after,
					sort,
				),
			fetchTransactionsCount: (filter) =>
				getScenarioTransactionsCount(numericScenarioId, filter),
			fetchDataPoints: (filter, first, after, sort) =>
				getScenarioDataPointsGraphql(
					numericScenarioId,
					filter,
					first,
					after,
					sort,
				),
			fetchDataPointsCount: (filter) =>
				getScenarioDataPointsCount(numericScenarioId, filter),
			fetchDistinctValues: (field) =>
				getScenarioTransactionDistinctValues(numericScenarioId, field),
			getDemoTransactionRows: getScenarioDemoTransactionRows,
			getDemoBalanceRows: () =>
				getScenarioDemoBalanceRows(baseForecast, scenarioChanges),
			getDemoTransactionOptions: getScenarioDemoTransactionOptions,
			// Scenario data is materialised per scenario and already scoped to the
			// scenario's forecast horizon. Scope every query (and the Excel export,
			// which sends this filter straight to the backend) to this scenario.
			horizonFilter: () => ({ scenarioId: { eq: numericScenarioId } }),
			horizonKey: String(numericScenarioId),
		}),
		[numericScenarioId, baseForecast, scenarioChanges],
	);

	return (
		<TransactionsBalancesTable
			source={source}
			initialColumnFilters={initialColumnFilters}
			dateFilter={dateFilter}
			quickFilterRanges={quickFilterRanges}
			refreshSignal={refreshSignal}
		/>
	);
};
